import { useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchApiWithToken, type ResponseError } from '~/api/utils'
import type { ConfigName, ConfigPayload, ConfigResponse } from '../types'

export const useUpdateConfig = (configName?: ConfigName) => {
  const queryClient = useQueryClient()
  return useMutation<
    ConfigResponse,
    ResponseError,
    Omit<ConfigPayload, 'name'> & { name?: ConfigName }
  >({
    mutationFn: (data) => {
      const name = configName ?? data.name
      if (!name) {
        throw new Error('Config name is required')
      }
      const configData: ConfigPayload = {
        ...data,
        name,
        value: data.value
      }
      if (!configData.name) {
        throw new Error('Config name is required')
      }
      return fetchApiWithToken<ConfigResponse>({
        method: 'put',
        url: '/v1/configuration',
        data: configData
      })
    },
    onMutate: async (variables) => {
      const configNameKey = configName ?? (variables.name as ConfigName)
      const agencyId = variables.agency
        ? Number.parseInt(String(variables.agency).split('/').pop() ?? '', 10)
        : undefined
      const cacheKey =
        agencyId !== undefined && !Number.isNaN(agencyId)
          ? ['config', configNameKey, agencyId]
          : ['config', configNameKey]

      await queryClient.cancelQueries({ queryKey: cacheKey })

      const previousConfig = queryClient.getQueryData<ConfigResponse | null>(
        cacheKey
      )

      queryClient.setQueryData(cacheKey, variables.value)

      return { previousConfig, cacheKey }
    },
    onError: (_error, _variables, context) => {
      if (
        context &&
        typeof context === 'object' &&
        'previousConfig' in context &&
        'cacheKey' in context &&
        context.previousConfig
      ) {
        queryClient.setQueryData(
          context.cacheKey as string[],
          context.previousConfig
        )
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['adminConfig', configName ?? variables.name]
      })
    },
    onSuccess: (data, variables) => {
      const configNameKey = configName ?? variables.name
      const agencyId = variables.agency
        ? Number.parseInt(String(variables.agency).split('/').pop() ?? '', 10)
        : undefined
      const cacheKey =
        agencyId !== undefined && !Number.isNaN(agencyId)
          ? ['config', configNameKey, agencyId]
          : ['config', configNameKey]
      queryClient.setQueryData(cacheKey, data.value)
    },
    scope: {
      id: 'configuration-update'
    }
  })
}
